import { Column, GridApi } from "ag-grid-community";
import { Feature, FeatureCollection, Geometry } from "geojson";
import set from "lodash/set";

import * as turf from "fond/turf";
import { Maybe } from "fond/types";

/**
 * Build a GeoJSON FeatureCollection from the current AG Grid state.
 */
export function getDataAsGeoJson<TData>(api: GridApi<TData>, flatten: boolean, getGeometry: (data: TData) => Maybe<Geometry>): FeatureCollection {
  const paths: { colKey: Column; path: string[] }[] = [];
  api.getAllDisplayedColumns().forEach((colKey) => {
    const { headerName } = colKey.getColDef();
    if (!headerName) return;

    const parentHeaderName = colKey.getParent()?.getColGroupDef()?.headerName;
    let path = parentHeaderName ? [parentHeaderName, headerName] : [headerName];
    if (flatten) {
      path = [path.join(" - ")];
    }
    paths.push({ colKey, path });
  });

  const features: Feature[] = [];
  api.forEachNodeAfterFilterAndSort((rowNode) => {
    const geometry = (rowNode.data ? getGeometry(rowNode.data) : null) ?? null;

    const properties = {};
    paths.forEach(({ colKey, path }) => {
      set(properties, path, api.getCellValue({ rowNode, colKey }) ?? null);
    });

    features.push(turf.feature(geometry, properties, { id: rowNode.id ?? null }));
  });

  return turf.featureCollection(features);
}

/**
 * Export grid data as a GeoJSON file download.
 */
export function exportDataAsGeoJson<TData>(api: GridApi<TData>, flatten: boolean, getGeometry: (data: TData) => Maybe<Geometry>): void {
  const featureCollection = getDataAsGeoJson(api, flatten, getGeometry);
  const featureCollectionJSON = JSON.stringify(featureCollection);
  const blob = new Blob([featureCollectionJSON], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "export.geojson";
  link.click();
}
